import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from "../utils/supabase"
import { getMerchantForOwner } from "../utils/merchantUtils"
import {
	Box,
	Card,
	CardContent,
	Container,
	useMediaQuery,
	useTheme
} from '@mui/material'
import MerchantEditor from '../components/MerchantEditor'
import ServicesManager from '../components/ServicesManager'
import AddAppointment from '../components/AddAppointment'
import CalendarView from '../components/CalendarView'
import ClientsManager from '../components/ClientsManager'
import EmployeesManager from '../components/EmployeesManager'
import AppointmentDetailsDialog from '../components/AppointmentDetailsDialog'
import BookingManager from '../components/BookingManager'
import MessagesManager from '../components/MessagesManager'
import NavigationSidebar from '../components/NavigationSidebar'
import Dashboard from '../components/Dashboard'
import Agreements from '../pages/Agreements'
import MerchantTopBar from '../components/MerchantTopBar'
import Settings from '../pages/Settings'
import { LandingPageSettings } from '../components/merchant-landing-page'
import { useUnreadMessages } from '../utils/useUnreadMessages'
import { useNotificationFeed } from '../utils/useNotificationFeed'
import NotificationsPanel from '../components/NotificationsPanel'
import InvoicesManager from '../components/invoices/InvoicesManager'
import { ShieldCheck } from 'lucide-react'
import AdminManager from '../components/admin/AdminManager'

// Height of the "Acting as …" banner shown in admin act-as mode.
const ACT_AS_BANNER_H = 36

// Tab name to index mapping
const TAB_NAMES = {
	'calendar': 0,
	'clients': 1,
	'employees': 2,
	'services': 3,
	'booking': 4,
	'messages': 5,
	'settings': 6,
	'landingpage': 7,
	'dashboard': 8,
	'agreements': 9,
	'invoices': 10,
	'admin': 11
}

// Index to tab name mapping
const TAB_INDICES = {
	0: 'calendar',
	1: 'clients',
	2: 'employees',
	3: 'services',
	4: 'booking',
	5: 'messages',
	6: 'settings',
	7: 'landingpage',
	8: 'dashboard',
	9: 'agreements',
	10: 'invoices',
	11: 'admin'
}


function MerchantDashboard() {
	const [session, setSession] = useState(null)
	const [merchant, setMerchant] = useState(null)
	const [editorOpen, setEditorOpen] = useState(false)
	const [addAppointmentOpen, setAddAppointmentOpen] = useState(false)
	const [selectedTab, setSelectedTab] = useState(0)
	const [appointmentDetailsOpen, setAppointmentDetailsOpen] = useState(false)
	const [selectedAppointment, setSelectedAppointment] = useState(null)
	const [sidebarOpen, setSidebarOpen] = useState(false)
	const [preselectedMessageClient, setPreselectedMessageClient] = useState(null)
	// Client (and optionally pet) to open in the Clients tab, set when an
	// appointment's client / pet card is tapped.
	const [profileTarget, setProfileTarget] = useState(null)
	// Invoice (payments row id) to open in the Invoices tab, set from a
	// completed appointment's "View invoice" action. `origin` remembers the
	// tab and appointment it was opened from so the invoice's back button
	// returns to that appointment rather than the Invoices list.
	const [invoiceTarget, setInvoiceTarget] = useState(null)
	const [invoiceOrigin, setInvoiceOrigin] = useState(null)
	const [pendingAppointments, setPendingAppointments] = useState([])
	const [clientsKey, setClientsKey] = useState(0)
	// The logged-in owner's own merchant. `merchant` is normally the same row,
	// but in admin act-as mode it is the merchant being acted for.
	const [ownMerchant, setOwnMerchant] = useState(null)
	const settingsHasUnsavedChangesRef = useRef(false)
	const landingPageHasUnsavedChangesRef = useRef(false)

	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	// Act-as mode (admins only, migration 39): ?as=<merchant id> runs this whole
	// dashboard — sidebar, top bar, notifications and every tab — for that
	// merchant. It lives in the URL so it survives reloads and can be linked.
	const asParam = searchParams.get('as')
	const searchParamsRef = useRef(searchParams)
	searchParamsRef.current = searchParams
	const actingAs = Boolean(ownMerchant && merchant && merchant.id !== ownMerchant.id)
	const bannerH = actingAs ? ACT_AS_BANNER_H : 0
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// Always-on unread-messages tracking: powers the sidebar badge, the
	// per-client dots in MessagesManager, and desktop notifications on new
	// inbound. Lives at this level so it persists across tab switches.
	const { unreadByClient, totalUnread, markRead, lastReadMap } = useUnreadMessages(merchant)

	// Combined notification feed (booking requests + client messages) powering
	// the topbar bell pop-up.
	const {
		notifications,
		unreadCount: notifUnreadCount,
		unreadMessageCount: notifUnreadMessageCount,
		approve: approveNotif,
		decline: declineNotif,
		openMessage: openNotifMessage,
		markMessagesRead: markNotifMessagesRead
	} = useNotificationFeed(merchant, pendingAppointments, { lastReadMap, markRead })

	// Mobile notifications are a full-screen overlay rather than a dropdown.
	const [mobileNotifOpen, setMobileNotifOpen] = useState(false)

	// Initialize selected tab from URL parameter
	useEffect(() => {
		const tabParam = searchParams.get('tab')
		if (tabParam && TAB_NAMES.hasOwnProperty(tabParam.toLowerCase())) {
			const tabIndex = TAB_NAMES[tabParam.toLowerCase()]
			setSelectedTab(tabIndex)
		}
	}, [searchParams])

	// The Admin tab is only for merchants flagged is_admin (migration 39). Anyone
	// else landing on ?tab=admin is sent to the calendar once the merchant loads.
	useEffect(() => {
		if (!merchant || selectedTab !== 11 || (merchant.is_admin && !actingAs)) return
		setSelectedTab(0)
		const next = new URLSearchParams(searchParams)
		next.set('tab', TAB_INDICES[0])
		setSearchParams(next)
	}, [merchant, actingAs, selectedTab, searchParams, setSearchParams])

	// Check authentication and load the merchant. Re-runs when ?as= changes
	// (entering / leaving act-as mode) so the whole page re-targets.
	useEffect(() => {
		let mounted = true
		const init = async () => {
			// Drop everything tied to the previous merchant while switching.
			setMerchant(null)
			setPendingAppointments([])
			setSelectedAppointment(null)
			setAppointmentDetailsOpen(false)
			setProfileTarget(null)
			setInvoiceTarget(null)
			setInvoiceOrigin(null)
			setPreselectedMessageClient(null)

			const { data } = await supabase.auth.getSession()
			if (!mounted) return
			setSession(data.session)

			if (!data.session) {
				navigate('/login')
				return
			}

			// Load merchant data
			try {
				// Oldest merchant row for this owner. Never maybeSingle(): it errors
				// when an owner has duplicate rows and leaves the dashboard empty.
				const merchantData = await getMerchantForOwner(data.session.user.id, '*')
				if (!mounted) return

				if (!merchantData) {
					console.error('No merchant account found')
					return
				}

				setOwnMerchant(merchantData)

				// Act-as: an admin opening another merchant's dashboard.
				if (asParam && asParam !== merchantData.id) {
					if (merchantData.is_admin) {
						const { data: target, error } = await supabase
							.from('merchant')
							.select('*')
							.eq('id', asParam)
							.maybeSingle()
						if (!mounted) return
						if (target && !error) {
							setMerchant(target)
							return
						}
						console.error('Act-as merchant not available:', error?.message || 'not found')
					}
					// Not an admin, or the merchant is unavailable: drop the param and
					// fall through to the owner's own dashboard.
					const next = new URLSearchParams(searchParamsRef.current)
					next.delete('as')
					setSearchParams(next, { replace: true })
				}

				setMerchant(merchantData)
			} catch (e) {
				console.error('Failed to load merchant data:', e.message)
			}
		}
		init()
		return () => { mounted = false }
	}, [navigate, asParam, setSearchParams])

	const signOut = async () => {
		await supabase.auth.signOut()
		window.location.assign('/login')
	}

	// Admin picked a merchant in the Admin tab: open that merchant's dashboard.
	const enterActAs = (target) => {
		if (!target?.id) return
		const next = new URLSearchParams(searchParams)
		if (ownMerchant && target.id === ownMerchant.id) {
			next.delete('as')
		} else {
			next.set('as', target.id)
		}
		next.set('tab', TAB_INDICES[0])
		setSearchParams(next)
	}

	// Leave act-as mode and return to the admin's own Admin tab.
	const exitActAs = () => {
		if (settingsHasUnsavedChangesRef.current || landingPageHasUnsavedChangesRef.current) {
			const confirmLeave = window.confirm(
				'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.'
			)
			if (!confirmLeave) return
			settingsHasUnsavedChangesRef.current = false
			landingPageHasUnsavedChangesRef.current = false
		}
		const next = new URLSearchParams(searchParams)
		next.delete('as')
		next.set('tab', 'admin')
		setSearchParams(next)
	}

	const handleSaved = (m) => {
		setMerchant(m)
	}

	const handleAppointmentAdded = () => {
		// This will be handled by the FutureAppointments component
		// when it gets refreshed
	}

	const handleAppointmentClick = (appointment) => {
		setSelectedAppointment(appointment)
		setAppointmentDetailsOpen(true)
	}

	const handleAppointmentDetailsClose = () => {
		setAppointmentDetailsOpen(false)
		setSelectedAppointment(null)
		// The FutureAppointments component will handle its own refresh
	}

	const handleTabChange = (event, newValue) => {
		// Check for unsaved changes when navigating away from Settings tab
		if (selectedTab === 6 && newValue !== 6 && settingsHasUnsavedChangesRef.current) {
			const confirmLeave = window.confirm(
				'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.'
			)
			if (!confirmLeave) {
				return // User cancelled, stay on current tab
			}
			settingsHasUnsavedChangesRef.current = false
		}

		// Check for unsaved changes when navigating away from Landing Page tab
		if (selectedTab === 7 && newValue !== 7 && landingPageHasUnsavedChangesRef.current) {
			const confirmLeave = window.confirm(
				'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.'
			)
			if (!confirmLeave) {
				return // User cancelled, stay on current tab
			}
			landingPageHasUnsavedChangesRef.current = false
		}

		setSelectedTab(newValue)

		// Update URL parameter with human-readable tab name
		const newSearchParams = new URLSearchParams(searchParams)
		const tabName = TAB_INDICES[newValue]
		if (tabName) {
			newSearchParams.set('tab', tabName)
			setSearchParams(newSearchParams)
		}

		// Reset clients view when clicking clients tab
		if (newValue === 1) {
			setClientsKey(prev => prev + 1)
		}

	}

	// Jump to a client's profile — optionally focused on one of their pets.
	// There is no standalone pet page, so a pet opens its owner's profile with
	// that pet's card scrolled into view.
	const goToProfile = (client, petId = null) => {
		if (!client?.id) return
		setProfileTarget({ client, petId })
		setSelectedTab(1)
		const newSearchParams = new URLSearchParams(searchParams)
		newSearchParams.set('tab', 'clients')
		setSearchParams(newSearchParams)
	}

	const handleOpenClientProfile = (client) => goToProfile(client)
	const handleOpenPetProfile = ({ petId, client }) => goToProfile(client, petId)

	const switchTab = (index) => {
		setSelectedTab(index)
		const newSearchParams = new URLSearchParams(searchParams)
		newSearchParams.set('tab', TAB_INDICES[index])
		setSearchParams(newSearchParams)
	}

	// Jump to a paid appointment's invoice in the Invoices tab.
	const handleOpenInvoice = (payment, appointment = null) => {
		if (!payment?.id) return
		setInvoiceTarget(payment.id)
		setInvoiceOrigin(appointment ? { tab: selectedTab, appointment } : null)
		switchTab(10)
	}

	// Back from an invoice opened via "View invoice": return to the tab the
	// appointment lived on and reopen its details panel.
	const handleBackToAppointment = () => {
		const origin = invoiceOrigin
		setInvoiceOrigin(null)
		if (!origin) return
		switchTab(origin.tab)
		setSelectedAppointment(origin.appointment)
		setAppointmentDetailsOpen(true)
	}

	const handleMessageClient = (client) => {
		setPreselectedMessageClient(client)
		setSelectedTab(5) // Switch to messages tab
		// Update URL parameter
		const newSearchParams = new URLSearchParams(searchParams)
		newSearchParams.set('tab', 'messages')
		setSearchParams(newSearchParams)
	}


	// Open a message notification: mark its thread read and jump to Messages.
	const handleOpenNotifMessage = (n) => {
		if (!n?.clientId) return
		openNotifMessage(n.clientId)
		handleMessageClient({ id: n.clientId })
	}

	return (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
			{/* Desktop: Full-bleed app shell */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				flexGrow: 1,
				overflow: 'hidden',
				background: isMobile ? 'transparent' : '#F5F3FB',
				p: 0,
				gap: 0
			}}>
				{/* Main Content Area with Sidebar */}
				<Box sx={{
					display: 'flex',
					flexGrow: 1,
					overflow: 'hidden',
					gap: 0
				}}>
					{/* Desktop: Sidebar */}
					{!isMobile && (
						<NavigationSidebar
							open={sidebarOpen}
							onClose={() => setSidebarOpen(false)}
							selectedTab={selectedTab}
							onTabChange={handleTabChange}
							isMobile={isMobile}
							merchant={merchant}
							showAdmin={Boolean(merchant?.is_admin) && !actingAs}
							pendingAppointments={pendingAppointments}
							onPendingAppointmentsChange={setPendingAppointments}
							unreadMessagesCount={totalUnread}
						/>
					)}

					{/* Mobile: Overlay sidebar */}
					{isMobile && (
						<NavigationSidebar
							open={sidebarOpen}
							onClose={() => setSidebarOpen(false)}
							selectedTab={selectedTab}
							onTabChange={handleTabChange}
							isMobile={isMobile}
							merchant={merchant}
							showAdmin={Boolean(merchant?.is_admin) && !actingAs}
							pendingAppointments={pendingAppointments}
							onPendingAppointmentsChange={setPendingAppointments}
							unreadMessagesCount={totalUnread}
						/>
					)}

					{/* Main column: top bar (beside sidebar) + content */}
					<Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '0 !important' }}>
						{/* Act-as banner: always visible while an admin works as another merchant. */}
						{actingAs && (
							<Box sx={{
								flex: 'none', height: ACT_AS_BANNER_H, boxSizing: 'border-box',
								display: 'flex', alignItems: 'center', gap: '10px',
								px: isMobile ? '12px' : '20px', bgcolor: '#5F4EC2', color: '#FFFFFF',
								fontFamily: "'Outfit', sans-serif", fontSize: '13px', fontWeight: 650,
							}}>
								<ShieldCheck size={15} strokeWidth={1.9} style={{ flex: 'none' }} />
								<Box sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
									Acting as <strong>{merchant?.name}</strong>
									{!isMobile && <span style={{ opacity: 0.85 }}> · everything you do here applies to that merchant</span>}
								</Box>
								<Box
									component="button"
									type="button"
									onClick={exitActAs}
									sx={{
										flex: 'none', height: 24, px: '10px', borderRadius: '7px', cursor: 'pointer',
										border: '1px solid rgba(255,255,255,0.45)', bgcolor: 'rgba(255,255,255,0.12)',
										color: '#FFFFFF', fontFamily: 'inherit', fontSize: '12px', fontWeight: 700,
										'&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
									}}
								>
									Exit
								</Box>
							</Box>
						)}
						{/* Top Bar — spans the main column, to the right of the sidebar. Hidden on mobile for the Calendar tab, which has its own top bar. */}
						{(!isMobile || selectedTab !== 0) && (
							<MerchantTopBar
								merchant={merchant}
								notifications={notifications}
								unreadCount={notifUnreadCount}
								unreadMessageCount={notifUnreadMessageCount}
								onApprove={approveNotif}
								onDecline={declineNotif}
								onOpenMessage={handleOpenNotifMessage}
								onMarkMessagesRead={markNotifMessagesRead}
								onMenuClick={() => setSidebarOpen(true)}
								onNotificationsClick={() => setMobileNotifOpen(true)}
							/>
						)}
						<Container maxWidth={false} sx={{ flexGrow: 1, padding: '0 !important', height: actingAs ? 'auto' : '100%', minHeight: 0, overflow: 'hidden' }}>
							<Card sx={{
								height: isMobile
									? (selectedTab === 0 ? '100%' : `calc(100vh - ${64 + bannerH}px)`)
									: '100%',
								// Use dvh on mobile to fix Android cut-off: 100vh can exceed visible area
								...(isMobile && selectedTab !== 0 && {
									'@supports (height: 100dvh)': { height: `calc(100dvh - ${64 + bannerH}px)` }
								}),
								display: 'flex',
								flexDirection: 'column',
								boxShadow: 0,
								borderRadius: isMobile ? 0 : '',
								bgcolor: 'transparent'
							}}>

								<CardContent sx={{
									flexGrow: 1,
									overflow: selectedTab === 0 ? 'hidden' : 'auto',
									height: isMobile && selectedTab === 0 ? '100%' : 'auto',
									display: isMobile && selectedTab === 0 ? 'flex' : 'block',
									flexDirection: 'column',
									'&.MuiCardContent-root': {
										...((selectedTab === 4 || selectedTab === 5 || selectedTab === 0 || selectedTab === 1 || selectedTab === 3 || selectedTab === 6 || selectedTab === 7 || selectedTab === 8 || selectedTab === 9 || selectedTab === 2 || selectedTab === 10 || selectedTab === 11) && {
											padding: '0 !important',
											paddingTop: '0 !important',
											paddingBottom: '0 !important',
											paddingLeft: '0 !important',
											paddingRight: '0 !important'
										})
									},
								}}>
									{/* Calendar View - Now Default Tab */}
									{selectedTab === 0 && merchant && (
										<CalendarView
											merchant={merchant}
											onOpenSidebar={() => setSidebarOpen(true)}
											onOpenClientProfile={handleOpenClientProfile}
											onOpenPetProfile={handleOpenPetProfile}
											onOpenInvoice={handleOpenInvoice}
										/>
									)}

									{/* Clients/Pets Management */}
									{selectedTab === 1 && merchant && (
										<ClientsManager
											key={clientsKey}
											merchant={merchant}
											onMessageClient={handleMessageClient}
											preselectedClient={profileTarget?.client}
											focusPetId={profileTarget?.petId}
											onPreselectConsumed={() => setProfileTarget(null)}
										/>
									)}

									{/* Employees Management */}
									{selectedTab === 2 && merchant && (
										<EmployeesManager merchant={merchant} />
									)}

									{/* Services Management */}
									{selectedTab === 3 && merchant && (
										<ServicesManager merchant={merchant} />
									)}

									{/* Booking Management */}
									{selectedTab === 4 && merchant && (
										<BookingManager
											merchant={merchant}
											onAppointmentClick={handleAppointmentClick}
											pendingAppointments={pendingAppointments}
											onOpenCalendar={() => setSelectedTab(0)}
											onMessageClient={handleMessageClient}
										/>
									)}

									{/* Messages */}
									{selectedTab === 5 && merchant && (
										<MessagesManager
											merchant={merchant}
											preselectedClient={preselectedMessageClient}
											onClientSelected={() => setPreselectedMessageClient(null)}
											unreadByClient={unreadByClient}
											markRead={markRead}
											onOpenClientProfile={handleOpenClientProfile}
										/>
									)}

									{/* Settings */}
									{selectedTab === 6 && merchant && (
										<Settings
											merchant={merchant}
											onSaved={handleSaved}
											hasUnsavedChangesRef={settingsHasUnsavedChangesRef}
										/>
									)}

									{/* Landing Page */}
									{selectedTab === 7 && merchant && (
										<LandingPageSettings
											merchant={merchant}
											onSaved={handleSaved}
											hasUnsavedChangesRef={landingPageHasUnsavedChangesRef}
										/>
									)}

									{/* Dashboard */}
									{selectedTab === 8 && merchant && (
										<Dashboard merchant={merchant} />
									)}

									{/* Agreements */}
									{selectedTab === 9 && merchant && (
										<Agreements merchant={merchant} />
									)}

									{/* Invoices */}
									{selectedTab === 10 && merchant && (
										<InvoicesManager
											merchant={merchant}
											openInvoiceId={invoiceTarget}
											onOpenConsumed={() => setInvoiceTarget(null)}
											onBackToOrigin={invoiceOrigin ? handleBackToAppointment : null}
										/>
									)}

									{/* Admin: pick a merchant to act as (merchant.is_admin only, not while already acting) */}
									{selectedTab === 11 && merchant?.is_admin && !actingAs && (
										<AdminManager merchant={merchant} onSelectMerchant={enterActAs} />
									)}
								</CardContent>
							</Card>
						</Container>
					</Box>
				</Box>
			</Box>
			{/* Mobile notifications: full-screen overlay */}
			{isMobile && mobileNotifOpen && (
				<NotificationsPanel
					variant="mobile"
					notifications={notifications}
					unreadCount={notifUnreadCount}
					unreadMessageCount={notifUnreadMessageCount}
					onApprove={approveNotif}
					onDecline={declineNotif}
					onOpenMessage={(n) => {
						setMobileNotifOpen(false)
						handleOpenNotifMessage(n)
					}}
					onMarkMessagesRead={markNotifMessagesRead}
					onClose={() => setMobileNotifOpen(false)}
				/>
			)}

			{/* Modals */}
			{session?.user?.id && (
				<>
					<MerchantEditor
						open={editorOpen}
						onClose={() => setEditorOpen(false)}
						ownerId={session.user.id}
						merchant={merchant}
						onSaved={handleSaved}
					/>
					{merchant?.id && (
						<>
							<AddAppointment
								open={addAppointmentOpen}
								onClose={() => setAddAppointmentOpen(false)}
								merchant={merchant}
								onAppointmentAdded={handleAppointmentAdded}
							/>
							<AppointmentDetailsDialog
								onOpenClientProfile={handleOpenClientProfile}
								onOpenPetProfile={handleOpenPetProfile}
								onOpenInvoice={handleOpenInvoice}
								open={appointmentDetailsOpen}
								onClose={handleAppointmentDetailsClose}
								appointment={selectedAppointment}
								merchant={merchant}
							/>
						</>
					)}
				</>
			)}
		</Box>
	)
}

export default MerchantDashboard 